import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  ArrowRight,
  CheckCircle,
  Shield,
  Zap,
  Brain,
  BarChart3,
  Activity,
  Lock,
  Play,
  LineChart,
  Bot,
  Target,
  Network,
  Eye,
  Globe
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { cryptoTradingPlatformTestimonials } from '../../shared/testimonialData';

// CryptoTradingPlatform Testimonial Component
const CryptoTradingTestimonials: React.FC = () => {
  return (
    <section className="relative py-24">
      <ProductTestimonials 
        testimonials={cryptoTradingPlatformTestimonials}
        title="Crypto Trading Success Stories"
        subtitle="See how traders achieved exceptional ROI with our AI platform"
        gradientFrom="#FFD700"
        gradientVia="#8A3FFC"
        gradientTo="#00CFFF"
        iconColor="#FFD700"
        dotColor="bg-yellow-500"
        speed={70}
        direction="left"
      />
    </section>
  );
};

const CryptoTradingPlatform: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Brain,
      title: 'Unified Trading Engine ',
      description: 'High-performance order matching for spot, margin, and derivatives products, with support for market, limit, stop, and algorithmic orders in one interface.'
    },
    {
      icon: BarChart3,
      title: 'Professional Trading Workspaces ',
      description: 'Customizable “standard” and “pro” views, including depth charts, order-book ladders, advanced charting, and multi-screen layouts for active traders.'
    },
    {
      icon: Shield,
      title: 'Leverage & Derivatives Suite ',
      description: 'Configurable leverage limits, perpetual futures and options on major pairs, portfolio margin modes, and real-time P&L and liquidation analytics.'
    },
    {
      icon: Network,
      title: 'Yield & Treasury Hub ',
      description: 'Integrated yield products (staking, flexible savings, and structured notes) so treasuries can put idle balances to work within defined risk limits.'
    },
    {
      icon: Activity,
      title: 'Smart Routing & Liquidity Aggregation ',
      description: 'Optional connectivity to external venues and market makers, allowing firms to execute through QuantArchon while tapping broader market liquidity.'
    },
    {
      icon: Lock,
      title: 'Automation & APIs ',
      description: 'Low-latency REST and WebSocket APIs for market data, trading, and account management, plus a strategy sandbox for backtesting and deploying custom algos.'
    }
  ];

  const solutions = [
    {
      icon: Zap,
      title: '	Crypto & Quant Funds',
      description: 'QuantArchon acts as the primary execution and reporting hub, connecting portfolio managers, quants, and operations. Funds can run high-frequency strategies via API, allocate to yield products for cash management, and provide LPs with clean performance and risk reporting from a single source of truth.',
    },
    {
      icon: LineChart,
      title: '	Retail Brokers & Neo-Banks',
      description: 'Brokers and fintech apps can plug into QuantArchon as a crypto infrastructure layer. They handle the customer experience, while QuantArchon handles trading, risk, and liquidity. This shortens time-to-market for new crypto offerings and lets teams add digital assets without building a full exchange from scratch.',
    },
    {
      icon: Brain,
      title: '	Corporate Treasuries & Payment Companies',
      description: 'Treasurers can manage operational crypto balances used for settlements, cross-border payments, or loyalty programs. QuantArchon provides controlled access for a small group of authorized users, exposure limits per asset, and yield options for surplus balances, all with audit-ready logs.',
    },
    {
      icon: Target,
      title: '	Proprietary Trading Desks & Market Makers',
      description: 'Desks use QuantArchon for low-latency execution, advanced order types, and real-time risk. Multi-desk account structures separate strategies while preserving centralized oversight. Integration with external venues supports cross-exchange arbitrage and liquidity-provision strategies.',
    },
    {
      icon: Network,
      title: 'Exchanges & OTC Desks (White-Label Mode)',
      description: 'Smaller exchanges and OTC desks can deploy QuantArchon as a white-label engine with their own branding. They benefit from a proven trading core, risk controls, and product catalogue, while focusing their resources on customer acquisition and local partnerships.',
    }
  ];

  const compliances = [
    {
      icon: BarChart3,
      title: 'Institutional-Grade Custody Architecture',
      description: 'Asset storage combines cold, warm, and hot-wallet layers, with optional MPC-based custody and configurable withdrawal policies. Firms can align wallet policies with internal risk and segregation rules.',
    },
    {
      icon: Bot,
      title: 'Strong Identity & Access Controls',
      description: 'Multi-factor authentication, hardware-key support, IP and device whitelists, and granular role-based permissions ensure that only authorized team members can trade, approve withdrawals, or change risk settings.',
    },
    {
      icon: Shield,
      title: 'Compliance Toolkit',
      description: 'Integrated KYC/KYB workflows, transaction-monitoring hooks, and travel-rule-ready data fields help institutions align with AML and sanctions requirements in their jurisdictions. Audit logs track every sensitive action for internal review.',
    },
    {
      icon: Globe,
      title: 'Real-Time Risk & Limits',
      description: 'Margin, leverage, and position limits are enforced in real time at user, desk, and entity levels. Automated monitoring flags unusual behavior, large exposures, or concentration risk, supporting both front-office and risk-office needs.',
    },
    {
      icon: CheckCircle,
      title: 'Data Protection & Governance',
      description: 'All sensitive data is encrypted in transit and at rest, with strict separation between client environments. Configurable retention policies and export tools help risk, legal, and finance teams meet their own governance and reporting obligations.',
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Stronger Execution and Liquidity',
      description: 'A single, high-performance trading core and optional external routing improve price discovery and reduce slippage, directly impacting trading P&L and hedging efficiency.',
      metric: '24/7 Active'
    },
    {
      icon: TrendingUp,
      title: 'Operational Efficiency Across Teams',
      description: 'Bringing trading, risk, treasury, and reporting into one platform cuts down on manual reconciliations, spreadsheet risk, and context-switching between tools. Teams spend more time on strategy and less on plumbing.',
      metric: 'Profit Optimization'
    },
    {
      icon: Shield,
      title: 'aster Time-to-Market for New Products',
      description: 'Whether it’s launching derivatives, adding new assets, or integrating crypto into a retail app, QuantArchon shortens build cycles. Businesses can test and release new offerings while relying on an established back end.',
      metric: 'Capital Safe'
    },
    {
      icon: Eye,
      title: '	Controlled Exposure and Reduced Risk Events',
      description: 'Real-time margining, limits, and monitoring reduce the frequency and severity of unwanted liquidations or operational errors. Senior stakeholders gain a clearer view of where risk is building up.',
      metric: 'Data-Driven'
    },
    {
      icon: Globe,
      title: 'Institutional Credibility with Stakeholders',
      description: 'A transparent, governed trading stack reassures boards, auditors, and regulators that digital-asset activities are being handled with the same discipline as traditional markets, supporting bigger mandates over time.',
      metric: 'Global Access'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-purple-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 neon-glow">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-400 uppercase tracking-wide">AI-Powered Trading</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">Crypto Trading Platform</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Intelligent Cryptocurrency Trading</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               QuantArchon is a full-stack crypto trading platform designed for teams that treat digital assets as a serious business line, not a side experiment. It brings together spot, margin, and derivatives trading, alongside automated strategies, yield products, and portfolio tools, into a single environment that can serve both a small trading desk and a global institution. QuantArchon is a full-stack crypto trading platform designed for teams that treat digital assets as a serious business line, not a side experiment.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
                >
                  Start Trading Today
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-500">200+</div>
                  <div className="text-sm text-muted-foreground">Cryptocurrencies</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-pink-400">50+</div>
                  <div className="text-sm text-muted-foreground">Exchanges</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Trading</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/CryptoTrading.png"
                    alt="Crypto Trading Platform Dashboard"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Professional-grade tools for cryptocurrency trading
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/*Solutions Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Advanced AI-powered strategies for every trading style
            </p>
          </motion.div>

           <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{solution.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Compliance </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto  text-center">
              Enterprise-grade infrastructure for professional trading
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-purple-500/50 hover:border-purple-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform your cryptocurrency trading strategy
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-purple-500/50 hover:border-purple-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CryptoTradingTestimonials />
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-purple-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Trading Strategy?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Join thousands of traders using AI-powered automation to maximize returns and minimize risk in the cryptocurrency markets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 text-white"
                >
                  Request Demo
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CryptoTradingPlatform;